import { useId, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import { Close, ImageSearch } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * Failure Mode Reference (mockup: Rock TCS result entry 2.png — "Failure Mode
 * Reference"). The four ASTM D7012 / ISRM compression failure modes drawn as
 * crisp core-cylinder schematics. The card matching the engineer's selected
 * `failureMode` highlights automatically; the combo modes
 * ("Shear & Axial Cleavage", "Multiple Shear & Axial Cleavage") light up their
 * two component cards. A small icon opens an enlarged dialog.
 *
 * All colours come from `theme` tokens — no hex literals (CODING-STANDARDS):
 *   fracture planes → error.main · load/spall arrows → schedule.machineTime
 *   highlight → primary.main · body/top/stroke → neutral surface tokens.
 */

type FailureBase = 'Shear' | 'Multiple Shear' | 'Axial Cleavage' | 'Fragmented';

const BASE_MODES: { base: FailureBase; desc: string }[] = [
  { base: 'Shear', desc: 'A single inclined shear plane through the specimen.' },
  { base: 'Multiple Shear', desc: 'Two or more conjugate shear planes (X-pattern).' },
  { base: 'Axial Cleavage', desc: 'Vertical splitting parallel to the load axis.' },
  { base: 'Fragmented', desc: 'Pervasive intersecting fractures; specimen breaks into pieces.' },
];

/** Which base diagrams light up for each selectable failure mode. */
const ACTIVE_BASES: Record<string, FailureBase[]> = {
  'Shear': ['Shear'],
  'Multiple Shear': ['Multiple Shear'],
  'Axial Cleavage': ['Axial Cleavage'],
  'Shear & Axial Cleavage': ['Shear', 'Axial Cleavage'],
  'Multiple Shear & Axial Cleavage': ['Multiple Shear', 'Axial Cleavage'],
  'Fragmented': ['Fragmented'],
};

interface GlyphColors { fracture: string; load: string; body: string; top: string; stroke: string; }

/** One core-cylinder schematic for a single base failure mode. */
const FailureModeGlyph = ({ mode, c }: { mode: FailureBase; c: GlyphColors }) => {
  // useId() returns ":r0:"-style ids whose colons are invalid inside url(#…);
  // strip them so the marker references resolve.
  const uid = useId().replace(/:/g, '');
  const red = `frx-${uid}`;
  const slate = `ld-${uid}`;
  return (
    <svg viewBox="0 0 120 160" role="img" aria-label={`${mode} failure mode`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <marker id={red} markerUnits="userSpaceOnUse" markerWidth="11" markerHeight="11" refX="7.5" refY="5.5" orient="auto">
          <path d="M0,0 L9,5.5 L0,11 Z" fill={c.fracture} />
        </marker>
        <marker id={slate} markerUnits="userSpaceOnUse" markerWidth="11" markerHeight="11" refX="7.5" refY="5.5" orient="auto">
          <path d="M0,0 L9,5.5 L0,11 Z" fill={c.load} />
        </marker>
      </defs>

      {/* Axial load arrows sit above the cylinder for the cleavage case */}
      {mode === 'Axial Cleavage' && (
        <>
          <line x1="51" y1="6" x2="51" y2="20" stroke={c.load} strokeWidth="2" markerEnd={`url(#${slate})`} />
          <line x1="69" y1="6" x2="69" y2="20" stroke={c.load} strokeWidth="2" markerEnd={`url(#${slate})`} />
        </>
      )}

      {/* Cylinder body + top rim */}
      <path d="M24,32 L24,128 A36,11.5 0 0 0 96,128 L96,32" fill={c.body} stroke={c.stroke} strokeWidth="1.7" />
      <ellipse cx="60" cy="32" rx="36" ry="11.5" fill={c.top} stroke={c.stroke} strokeWidth="1.7" />

      {mode === 'Shear' && (
        <>
          <line x1="35" y1="48" x2="85" y2="116" stroke={c.fracture} strokeWidth="3" strokeLinecap="round" />
          <line x1="71.9" y1="83.1" x2="62.5" y2="70.3" stroke={c.fracture} strokeWidth="2" markerEnd={`url(#${red})`} />
          <line x1="48.1" y1="80.9" x2="57.5" y2="93.7" stroke={c.fracture} strokeWidth="2" markerEnd={`url(#${red})`} />
        </>
      )}

      {mode === 'Multiple Shear' && (
        <>
          <line x1="35" y1="48" x2="85" y2="116" stroke={c.fracture} strokeWidth="2.7" strokeLinecap="round" markerEnd={`url(#${red})`} />
          <line x1="85" y1="48" x2="35" y2="116" stroke={c.fracture} strokeWidth="2.7" strokeLinecap="round" markerEnd={`url(#${red})`} />
        </>
      )}

      {mode === 'Axial Cleavage' && (
        <>
          <line x1="51" y1="44" x2="51" y2="122" stroke={c.fracture} strokeWidth="2.8" strokeLinecap="round" />
          <line x1="69" y1="44" x2="69" y2="122" stroke={c.fracture} strokeWidth="2.8" strokeLinecap="round" />
        </>
      )}

      {mode === 'Fragmented' && (
        <>
          <g stroke={c.fracture} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none">
            <path d="M58,44 L52,72 L60,96 L54,120" />
            <path d="M52,72 L34,62" />
            <path d="M52,72 L80,58" />
            <path d="M60,96 L80,112" />
            <path d="M60,96 L40,112" />
          </g>
          <line x1="30" y1="52" x2="16" y2="42" stroke={c.load} strokeWidth="2" markerEnd={`url(#${slate})`} />
          <line x1="90" y1="52" x2="104" y2="42" stroke={c.load} strokeWidth="2" markerEnd={`url(#${slate})`} />
          <line x1="30" y1="112" x2="16" y2="122" stroke={c.load} strokeWidth="2" markerEnd={`url(#${slate})`} />
          <line x1="90" y1="112" x2="104" y2="122" stroke={c.load} strokeWidth="2" markerEnd={`url(#${slate})`} />
        </>
      )}
    </svg>
  );
};

interface Props {
  /** The currently selected failure mode (drives which card highlights). */
  selectedMode: string | null;
}

export const FailureModeReference = ({ selectedMode }: Props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const colors: GlyphColors = {
    fracture: theme.palette.error.main,
    load: theme.schedule.machineTime,
    body: theme.table.stripeBg,
    top: theme.palette.background.paper,
    stroke: theme.palette.text.secondary,
  };

  const isActive = (base: FailureBase) =>
    selectedMode ? (ACTIVE_BASES[selectedMode] ?? []).includes(base) : false;

  const renderCards = (variant: 'compact' | 'large') => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: variant === 'large' ? 2 : 1 }}>
      {BASE_MODES.map(({ base, desc }) => {
        const on = isActive(base);
        return (
          <Box
            key={base}
            sx={{
              border: on ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.border.color}`,
              backgroundColor: on ? theme.palette.background.default : theme.palette.background.paper,
              borderRadius: 1,
              p: on ? '5px 4px 4px' : '6px 5px 5px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: variant === 'large' ? 140 : 96 }}>
              <FailureModeGlyph mode={base} c={colors} />
            </Box>
            <Typography
              sx={{ fontSize: variant === 'large' ? '0.84rem' : '0.72rem', fontWeight: 600, color: 'text.primary', mt: 0.5, textAlign: 'center' }}
            >
              {base}
            </Typography>
            {variant === 'large' && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', textAlign: 'center', mt: 0.25 }}>
                {desc}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography
          variant="caption"
          sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}
        >
          Failure Mode Reference
        </Typography>
        <Tooltip title="View reference images">
          <IconButton size="small" onClick={() => setOpen(true)} sx={{ p: 0.25, color: 'primary.main' }}>
            <ImageSearch sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {renderCards('compact')}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, pr: 6 }}>
          Failure Mode Reference — ASTM D7012 / ISRM
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem', display: 'block', mb: 1.5 }}>
            Red lines mark fracture planes; slate arrows show load / spalling direction. Combination modes
            correspond to two of the diagrams below.
          </Typography>
          {renderCards('large')}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
